import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes';
import { downloadBlob } from './downloadBlob';
import { formatDate } from './formatDate';
import { notify } from './notify';

type UseReportRestApiProps = {
  slug: string;
  fileName?: string;
  onSuccess?: () => void;
};

const REST_API_HOST = process.env.REACT_APP_REST_API_HOST;

const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again.';
const REPORT_ERROR_MESSAGE =
  'The report could not be generated. Please try again.';

const ERROR_CONTENT_TYPES = [
  'text/html',
  'text/xml',
  'application/xml',
  'application/json',
];

const isErrorContentType = (contentType: string | null) =>
  !!contentType &&
  ERROR_CONTENT_TYPES.some((type) => contentType.includes(type));

const isUnauthorized = (apiResponse: Response) =>
  apiResponse.status === 401 ||
  apiResponse.status === 403 ||
  apiResponse.redirected;

const isFailedResponse = (apiResponse: Response) =>
  !apiResponse.ok ||
  apiResponse.redirected ||
  isErrorContentType(apiResponse.headers.get('content-type'));

const getDefaultFileName = () => `Report-${formatDate(new Date())}.csv`;

export const useReportRestApi = <T extends Record<string, unknown>>({
  fileName,
  onSuccess,
  slug,
}: UseReportRestApiProps) => {
  const navigate = useNavigate();
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleFailedResponse = (apiResponse: Response) => {
    if (isUnauthorized(apiResponse)) {
      setError(new Error(SESSION_EXPIRED_MESSAGE));
      notify(SESSION_EXPIRED_MESSAGE, { type: 'error' });
      navigate(paths.auth.signin);
      return;
    }

    setError(new Error(REPORT_ERROR_MESSAGE));
    notify(REPORT_ERROR_MESSAGE, { type: 'error' });
  };

  const getAPIData = async (payload: T) => {
    setLoading(true);
    setError(undefined);
    try {
      const apiResponse = await fetch(`${REST_API_HOST}${slug}`, {
        body: JSON.stringify(payload),
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'text/csv;charset=UTF-8',
        },
      });

      if (isFailedResponse(apiResponse)) {
        handleFailedResponse(apiResponse);
        return;
      }

      const blob = await apiResponse.blob();
      downloadBlob(blob, fileName || getDefaultFileName());
      onSuccess?.();
    } catch (err) {
      setError(err);
      notify(REPORT_ERROR_MESSAGE, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return { error, loading, getAPIData };
};
